/** @jsx jsx */
import { jsx } from "theme-ui";
import { useState, useLayoutEffect } from "react";
import { Grid, Cell } from "@components/primitives/Grid";
import { rem } from "@src/theme";
import { Link } from "gatsby";
import { useScrollPosition } from "@n8tb1t/use-scroll-position";
import { cellAfterLine, cellBeforeLine } from "@src/global-styles/verticalLine";
import styles from "./styles";
const Compass = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 15 15"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="7.5" cy="7.5" r="6.5" stroke="#2C3026" stroke-width="2" />
    <path
      d="M5.40037 3.85921C5.41602 3.16702 6.28116 2.86388 6.72542 3.39492L9.25321 6.41645C9.36985 6.55587 9.43188 6.73292 9.42777 6.91465L9.3387 10.8531C9.32305 11.5453 8.45791 11.8484 8.01365 11.3174L5.48587 8.29586C5.36922 8.15643 5.30719 7.97939 5.3113 7.79766L5.40037 3.85921Z"
      fill="#2C3026"
    />
  </svg>
);

const Hamburger = () => (
  <svg
    width="13"
    height="12"
    viewBox="0 0 13 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    sx={{
      transform: "translateY(-2px)",
    }}
  >
    <line y1="1" x2="13" y2="1" stroke="#2C3026" stroke-width="2" />
    <line y1="6" x2="13" y2="6" stroke="#2C3026" stroke-width="2" />
    <line y1="11" x2="13" y2="11" stroke="#2C3026" stroke-width="2" />
  </svg>
);

interface ComponentProps {
  fixedSidebar?: Boolean;
  onMobileMenuToggle: () => void;
  onMenuToggle: (menuType?: string) => void;
}

const Header = ({
  fixedSidebar = false,
  onMobileMenuToggle,
  onMenuToggle,
}: ComponentProps) => {
  const [scrollHidden, setScrollHidden] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);

  useScrollPosition(
    ({ currPos }) => {
      if (!fixedSidebar) {
        return;
      }
      if (contentHeight && currPos.y >= contentHeight + 113) {
        if (scrollHidden) {
          setScrollHidden(false);
        }
      } else {
        setScrollHidden(currPos.y > 1);
      }
    },
    [scrollHidden],
    null,
    true,
    80
  );

  useLayoutEffect(() => {
    const contentHeight = document.getElementById("content").offsetHeight;
    if (contentHeight) {
      setContentHeight(contentHeight);
    }
  }, []);

  return (
    <header>
      <Grid styles={styles.container}>
        <Cell
          gridColumn={["span 4", "span 4", "span 3"]}
          styles={{
            ...cellAfterLine,
            "&:after": {
              ...cellAfterLine["&:after"],
              display: ["none", null, "block"],
            },
            display: ["none", "flex"],
            alignItems: "center",
          }}
        >
          <div
            sx={{
              position: "relative",
            }}
          >
            <div
              sx={{
                position:
                  fixedSidebar && scrollHidden
                    ? ["static", null, "fixed"]
                    : "static",
                top: fixedSidebar && scrollHidden ? "30px" : 0,
              }}
            >
              <button
                sx={{
                  mb: rem(6),
                  fontFamily: "heading",
                  display: "flex",
                  cursor: "pointer",
                  alignItems: "center",
                  "&:hover": {
                    bg: "rgba(0, 0, 0, 0.08)",
                  },
                }}
                onClick={() => onMenuToggle("topics")}
              >
                <Hamburger />

                <span
                  sx={{
                    display: "block",
                    ml: rem(4),
                    transform: [
                      "translate(2px, 0px)",
                      null,
                      "translate(2px, 0px)",
                    ],
                  }}
                >
                  Browse topics
                </span>
              </button>
              <button
                sx={{
                  fontFamily: "heading",
                  display: "flex",
                  cursor: "pointer",
                  alignItems: "center",
                  "&:hover": {
                    bg: "rgba(0, 0, 0, 0.08)",
                  },
                }}
                onClick={() => onMenuToggle("regions")}
              >
                <Compass />

                <span
                  sx={{
                    display: "block",
                    ml: rem(4),
                    textAlign: "left",
                    whiteSpace: "nowrap",
                    transform: ["translateY(2px)", null, "translateY(2px)"],
                  }}
                >
                  Explore regions
                </span>
              </button>
            </div>
          </div>
        </Cell>
        <Cell
          gridColumn={["span 4", "5 / span 8", "4 / span 5"]}
          styles={{
            ...cellAfterLine,
            "&:after": {
              ...cellAfterLine["&:after"],
              display: ["none", null, "block"],
            },
          }}
        >
          <div
            sx={{
              display: "flex",
              justifyContent: ["space-between", "center"],
              borderBottom: fixedSidebar ? "none" : ["1px solid black", "none"],
              pb: fixedSidebar ? 0 : [rem(14), 0],
            }}
          >
            <div
              sx={{
                width: ["100%"],
                maxWidth: [rem(247), "100%", "100%"],
                flex: "1 1 auto",
                px: ["2.5vw", "0.5vw"],
                pl: [0],
                pr: [rem(20), "0.5vw"],
                transform: ["translateY(5px)", null, "none"],
              }}
            >
              <Link
                to="/"
                sx={{
                  "&:hover": {
                    bg: "transparent",
                  },
                }}
              >
                <svg
                  width="100%"
                  viewBox="0 0 310 61"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  sx={{
                    transform: ["none", null, "translateY(8px)"],
                  }}
                >
                  <g clip-path="url(#clip0)">
                    <path
                      d="M295.064 30.2191V37.2306C294.993 40.7364 293.655 43.3306 291.189 43.3306C289.216 43.3306 288.159 41.3674 288.159 38.072C288.159 33.8651 289.78 31.5513 293.162 30.6398L295.064 30.2191ZM310.071 44.5927C309.648 44.7329 309.155 44.8732 308.732 44.8732C306.266 44.8732 305.773 42.9801 305.773 39.8249C305.773 34.4962 305.843 29.8686 305.843 25.872C305.843 18.0191 300.489 15.2847 291.612 15.2847C284.355 15.2847 279.282 18.2295 279.282 23.1375C279.282 26.4329 281.818 28.3962 285.059 28.3962C287.948 28.3962 290.343 27.064 291.259 24.6801C290.907 24.8203 290.555 24.8904 290.273 24.8904C288.652 24.8904 287.596 23.6283 287.596 21.0341C287.596 18.4398 288.864 16.4766 291.259 16.4766C294.007 16.4766 294.993 18.9306 294.993 21.8755V29.1674L290.837 30.0088C284.002 31.4812 277.591 33.0939 277.591 39.9651C277.591 45.1536 281.466 47.7479 286.116 47.7479C290.907 47.7479 294.218 45.1536 295.134 41.1571C295.134 41.3674 295.275 42.349 295.275 42.4893C295.98 45.8548 298.375 47.8881 302.884 47.8881C305.843 47.8881 308.662 46.626 310 44.8732L310.071 44.5927ZM275.759 15.9157H262.162V16.0559C264.557 17.6686 264.98 18.5801 264.98 22.9272V40.4559C264.98 44.6628 264.768 45.5042 262.514 47.1168V47.2571H278.225V47.1168C275.971 45.5042 275.759 44.6628 275.759 40.4559V15.9157ZM264.205 6.23983C264.205 9.53524 266.812 12.1996 270.334 12.1996C273.857 12.1996 276.464 9.53524 276.464 6.23983C276.464 2.87432 273.787 0.280065 270.334 0.280065C266.812 0.280065 264.205 2.87432 264.205 6.23983ZM248.493 21.9456C247.366 17.7387 244.407 15.2847 239.827 15.2847C232.782 15.2847 227.287 21.7352 227.287 31.8318C227.287 41.718 232.5 47.818 239.123 47.818C243.984 47.818 247.296 44.5927 248.493 39.8249V47.187H261.809V47.0467C259.555 45.4341 259.273 44.3123 259.273 40.3157V-0.140625L245.464 -0.140625V-0.000395105C247.859 1.47202 248.423 2.52374 248.423 6.87087V21.9456H248.493ZM243.702 43.4008C240.462 43.4008 238.841 39.1939 238.841 31.7617C238.841 22.9973 240.391 18.4398 243.632 18.4398C246.309 18.4398 248.564 21.8755 248.564 27.695V35.0571C248.493 40.1054 246.309 43.4008 243.702 43.4008ZM193.398 15.9157V16.0559C195.793 17.6686 196.216 18.5801 196.216 22.9272V40.4559C196.216 44.6628 196.005 45.5042 193.75 47.1168V47.2571L209.532 47.2571V47.1168C207.277 45.5042 207.066 44.6628 207.066 40.4559V27.8352C207.066 22.9272 208.968 20.5433 211.505 20.5433C213.971 20.5433 214.957 22.4364 214.957 24.8904V40.3858C214.957 44.5927 214.746 45.4341 212.562 47.0467V47.187H228.202V47.0467C225.948 45.4341 225.737 44.5927 225.737 40.3858V25.241C225.737 19.5617 222.848 15.2847 216.789 15.2847C211.998 15.2847 208.405 18.1594 206.996 23.3479V15.9157H193.398ZM181.28 30.2191V37.2306C181.209 40.7364 179.871 43.3306 177.405 43.3306C175.432 43.3306 174.375 41.3674 174.375 38.072C174.375 33.8651 175.996 31.5513 179.377 30.6398L181.28 30.2191ZM196.287 44.5927C195.864 44.7329 195.371 44.8732 194.948 44.8732C192.482 44.8732 191.989 42.9801 191.989 39.8249C191.989 34.4962 192.059 29.8686 192.059 25.872C192.059 18.0191 186.705 15.2847 177.827 15.2847C170.571 15.2847 165.498 18.2295 165.498 23.1375C165.498 26.4329 168.034 28.3962 171.275 28.3962C174.164 28.3962 176.559 27.064 177.475 24.6801C177.123 24.8203 176.771 24.8904 176.489 24.8904C174.868 24.8904 173.812 23.6283 173.812 21.0341C173.812 18.4398 175.08 16.4766 177.475 16.4766C180.223 16.4766 181.209 18.9306 181.209 21.8755V29.1674L177.052 30.0088C170.218 31.4812 163.807 33.0939 163.807 39.9651C163.807 45.1536 167.682 47.7479 172.332 47.7479C177.123 47.7479 180.434 45.1536 181.35 41.1571C181.35 41.3674 181.491 42.349 181.491 42.4893C182.196 45.8548 184.591 47.8881 189.1 47.8881C192.059 47.8881 194.877 46.626 196.216 44.8732L196.287 44.5927ZM162.046 -0.140625L148.166 -0.140625V-0.000395105C150.562 1.47202 151.125 2.52374 151.125 6.87087V40.4559C151.125 44.6628 150.914 45.5042 148.73 47.1168V47.2571L164.512 47.2571V47.1168C162.257 45.5042 162.046 44.6628 162.046 40.4559V-0.140625ZM135.766 16.4065C139.923 16.4065 143.939 20.1927 146.546 25.7318H146.687V15.2145C145.63 16.1962 144.714 16.7571 143.375 16.7571C141.262 16.7571 139.218 15.2847 135.555 15.2847C129.637 15.2847 124.916 19.2812 124.916 25.241C124.916 31.2008 128.227 34.1456 135.202 36.6697C139.5 38.2123 141.755 39.264 141.755 42.2789C141.755 45.0835 139.852 46.7663 136.893 46.7663C132.102 46.7663 128.157 42.6996 124.916 35.9686H124.775L125.409 47.8881C126.255 47.187 127.03 46.4858 128.509 46.4858C130.623 46.4858 133.089 47.818 137.105 47.818C144.009 47.818 149.082 43.6812 149.082 37.2306C149.082 31.341 145.771 28.4663 138.443 25.8019C133.23 23.9088 131.609 22.9272 131.609 20.1226C131.539 17.949 133.159 16.4065 135.766 16.4065ZM102.089 41.2272C103.216 45.4341 106.246 47.8881 110.825 47.8881C117.8 47.8881 123.296 41.3674 123.296 31.2709C123.296 21.4548 118.082 15.3548 111.53 15.3548C106.668 15.3548 103.287 18.51 102.159 23.3479V15.9157L88.5615 15.9157V16.0559C90.957 17.6686 91.3797 18.5801 91.3797 22.9272L91.3797 54.2686C91.3797 58.4755 91.1683 59.3168 88.9138 60.9295V61.0697H104.907V60.9996C102.512 59.4571 102.089 58.5456 102.089 54.3387L102.089 41.2272V41.2272ZM106.95 19.7019C110.191 19.7019 111.812 23.9088 111.812 31.2709C111.812 40.1054 110.262 44.6628 107.021 44.6628C104.414 44.6628 102.089 41.2272 102.089 35.4077V28.1157C102.089 22.9973 104.343 19.7019 106.95 19.7019ZM68.2002 41.2272C69.3274 45.4341 72.357 47.8881 76.9365 47.8881C83.9115 47.8881 89.407 41.3674 89.407 31.2709C89.407 21.4548 84.1933 15.3548 77.6411 15.3548C72.7797 15.3548 69.3979 18.51 68.2706 23.3479V15.9157L54.6024 15.9157V16.0559C56.9979 17.6686 57.4206 18.5801 57.4206 22.9272V54.2686C57.4206 58.4755 57.2092 59.3168 54.9547 60.9295V61.0697L71.0183 61.0697V60.9996C68.6229 59.4571 68.2002 58.5456 68.2002 54.3387C68.2002 54.2686 68.2002 41.2272 68.2002 41.2272ZM73.0615 19.7019C76.3024 19.7019 77.9229 23.9088 77.9229 31.2709C77.9229 40.1054 76.3729 44.6628 73.132 44.6628C70.5252 44.6628 68.2002 41.2272 68.2002 35.4077V28.1157C68.2002 22.9973 70.4547 19.7019 73.0615 19.7019ZM53.2638 15.9157H39.6661V16.0559C42.0615 17.6686 42.4842 18.5801 42.4842 22.9272V40.4559C42.4842 44.6628 42.2729 45.5042 40.0183 47.1168V47.2571H55.8001V47.1168C53.5456 45.5042 53.3342 44.6628 53.3342 40.4559L53.3342 15.9157C53.3342 15.9858 53.2638 15.9858 53.2638 15.9157ZM41.7092 6.23983C41.7092 9.53524 44.3161 12.1996 47.8388 12.1996C51.3615 12.1996 53.9683 9.53524 53.9683 6.23983C53.9683 2.87432 51.2911 0.280065 47.8388 0.280065C44.3865 0.280065 41.7092 2.87432 41.7092 6.23983ZM22.6865 3.7157C28.182 3.7157 32.5501 9.88581 35.5797 17.7387H35.7911L35.3683 2.3134C34.1706 3.43524 33.2547 4.06627 31.6342 4.06627C29.5911 4.06627 26.632 2.3134 21.7001 2.3134C10.357 2.3134 -0.0703125 11.1479 -0.0703125 25.6617C-0.0703125 38.9134 8.24333 47.8881 19.8683 47.8881C25.2229 47.8881 29.8729 45.4341 32.3388 41.8582L38.0456 47.8881H38.1161V34.0054C38.1161 29.7985 38.5388 28.2559 41.0047 26.5732V26.4329L23.8138 26.4329V26.5732C26.4911 28.326 27.1956 30.0789 27.1956 34.0755V37.5812C27.1956 43.0502 24.8001 46.2054 20.9251 46.2054C15.0774 46.2054 12.5411 37.1605 12.5411 25.4513C12.5411 11.9191 15.8524 3.7157 22.6865 3.7157Z"
                      fill="black"
                    />
                    <path
                      d="M309.507 9.8161C309.507 11.8494 307.887 13.392 305.843 13.392C303.8 13.392 302.18 11.7793 302.18 9.8161C302.18 7.85288 303.8 6.24023 305.843 6.24023C307.957 6.31035 309.507 7.85288 309.507 9.8161ZM303.096 9.8161C303.096 11.4287 304.293 12.6908 305.914 12.6908C307.464 12.6908 308.662 11.4287 308.662 9.88621C308.662 8.27357 307.534 7.0115 305.914 7.0115C304.293 7.0115 303.096 8.27357 303.096 9.8161ZM305.28 11.7092H304.434V8.13334C304.787 8.06322 305.209 7.99311 305.843 7.99311C306.548 7.99311 306.83 8.13334 307.112 8.27357C307.323 8.4138 307.464 8.69426 307.464 9.04483C307.464 9.46552 307.182 9.74598 306.689 9.88621V9.95633C307.041 10.0966 307.252 10.377 307.323 10.7977C307.464 11.3586 307.464 11.569 307.605 11.7092H306.689C306.548 11.569 306.548 11.2184 306.407 10.8678C306.337 10.4471 306.125 10.3069 305.632 10.3069H305.209V11.7092H305.28ZM305.35 9.67587H305.773C306.266 9.67587 306.618 9.53564 306.618 9.18506C306.618 8.83449 306.407 8.62414 305.843 8.62414C305.632 8.62414 305.491 8.62414 305.35 8.69426V9.67587Z"
                      fill="black"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0">
                      <path d="M0 0L310 0V61L0 61L0 0Z" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </Link>
            </div>

            <button
              onClick={onMobileMenuToggle}
              sx={{
                display: ["flex", "none"],
                alignItems: "center",
                fontFamily: "heading",
                cursor: "pointer",
                pl: rem(20),
                borderLeft: ["1px solid", "none"],
                borderColor: "coal",
                justifyContent: "flex-end",
              }}
            >
              {" "}
              <span
                sx={{
                  display: "block",
                  mr: rem(8),
                  fontSize: rem(16),
                }}
              >
                Menu
              </span>
              <Hamburger />
            </button>
          </div>
        </Cell>
        <Cell
          gridColumn={["span 5"]}
          styles={{
            display: ["none", "none", "flex"],
            alignItems: "center",
            justifyContent: "center",
            "@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)": {
              display: "none",
            },
          }}
        >
          <p
            sx={{
              textAlign: "center",
              my: 0,
              lineHeight: 1.1,
              fontSize: ["2.5vw", "24px", "2.4vw", null, "2.15vw", rem(39)],
            }}
          >
            Connecting Gippsland through <br /> positive storytelling.
          </p>
        </Cell>

        <Cell
          gridColumn={["span 4", "13 / -1", "14 / -1"]}
          styles={{
            ...cellBeforeLine,
            "&:before": {
              ...cellBeforeLine["&:before"],
              display: ["none", null, "block"],
            },
            display: ["none", "flex"],
            alignItems: "center",
          }}
        >
          <div
            sx={{
              position: "relative",
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <div
              sx={{
                textAlign: "right",
                position:
                  fixedSidebar && scrollHidden
                    ? ["static", null, "fixed"]
                    : "static",
                top: fixedSidebar && scrollHidden ? "30px" : 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <a
                href="https://gippslandia.myshopify.com/collections/gippslandia-1"
                target="_blank"
                sx={{
                  my: 0,
                  fontFamily: "heading",
                  whiteSpace: "nowrap",
                  textDecoration: "none",
                  color: "inherit",
                  display: "inline-block",
                }}
              >
                Shop Gippslandia
              </a>
              <a
                href="https://dashboard.presspatron.com/donations/new?frame=1&t=wCUc2iNiCxZJTJjECwEcYDwN"
                target="_blank"
                sx={{
                  my: 0,
                  fontFamily: "heading",
                  whiteSpace: "nowrap",
                  textDecoration: "none",
                  color: "inherit",
                  display: "inline-block",
                }}
              >
                Support Gippslandia
              </a>
              {/* <Link
                to="/memberships"
                sx={{
                  my: 0,
                  fontFamily: "heading",
                  whiteSpace: "nowrap",
                  textDecoration: "none",
                  color: "inherit",
                  transform: "translateY(2px)",
                  display: "block",
                }}
              >
                Become a member
              </Link> */}
            </div>
          </div>
        </Cell>
      </Grid>
      <div sx={styles.descriptionBanner}>
        <p
          sx={{
            textAlign: "center",
            fontSize: rem(24),
            my: rem(4),
          }}
        >
          Connecting Gippsland through positive storytelling.
        </p>
      </div>
    </header>
  );
};

export default Header;
